import { Checkbox, useToast } from '@chakra-ui/react';
import { CheckCircle2, X, XCircle } from 'lucide-react';
import { Check } from 'lucide-react';
import { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'src/components/ui/button';
import {
  DialogFooter,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { SidebarMenuButton } from 'src/components/ui/sidebar';
import {
  deleteSelectedInstance,
  editVariableName,
  updateResponseSelection,
} from 'src/redux/providerBuilder/selectedInstances';
import { useAppSelector } from 'src/redux/hooks';
import {
  getSelectedNetworkRequest,
  getSelectedNetworkIndex,
} from 'src/redux/providerBuilder/selectedNetworkRequest/selectors';
import { getValueByPath } from 'src/utils/provider-builder/instance-selection-utils';
import { useDebounce } from 'src/utils/logs/send-logs';

interface ValidationResult {
  regex: boolean | null;
  jsonPath: boolean | null;
  xPath: boolean | null;
}

// Add this near the top of the file, after the interfaces
const VALIDATION_MESSAGES = {
  jsonPath: 'Invalid JSONPath expression',
  xPath: 'Invalid XPath expression',
  responseMatch: 'Invalid response match pattern',
} as const;

export default function DataModal({
  instance,
  instance_index,
}: {
  instance: any;
  instance_index: number;
}) {
  const toast = useToast();
  const selectedNetworkRequest = useAppSelector(getSelectedNetworkRequest);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);
  const [variableName, setVariableName] = useState(instance.variableName);
  const [description, setDescription] = useState(instance.responseSelection?.description);
  const [isPrivateInformation, setIsPrivateInformation] = useState(
    instance.responseSelection?.hash === 'oprf'
  );
  const [responseMatch, setResponseMatch] = useState(
    instance.responseSelection?.responseMatch
  );
  const [jsonPath, setJsonPath] = useState(instance.responseSelection?.jsonPath);
  const [modalOpen, setModalOpen] = useState(false);
  const [xPath, setXPath] = useState(instance.responseSelection?.xPath);
  const [validation, setValidation] = useState<ValidationResult>({
    regex: null,
    jsonPath: null,
    xPath: null,
  });
  const dispatch = useDispatch();

  const handleUpdateVariable = () => {
    // Validate variable name and description
    if (!variableName.trim() || !description?.trim()) {
      toast({
        title: 'Error',
        description: !variableName.trim()
          ? 'Variable name cannot be empty'
          : 'Description cannot be empty',
        status: 'error',
        position: 'top-right',
      });
      return;
    }

    // Content type validation
    const contentType = selectedNetworkRequest[selectedNetworkIndex]?.contentType || '';
    const isJson = contentType.includes('json');
    const isValid = isJson
      ? validation.jsonPath && validation.regex
      : validation.xPath && validation.regex;

    if (!isValid) {
      toast({
        title: 'Validation Error',
        description: `Please ensure both ${isJson ? 'JSONPath' : 'XPath'} and Response Match are valid`,
        status: 'error',
        position: 'top-right',
      });
      return;
    }

    // Update variable name if changed
    if (instance.variableName !== variableName && variableName !== '') {
      dispatch(editVariableName({ value: variableName, index: instance_index }));
    }

    // Create new response selection object with changed values only
    const newResponseSelection = {
      ...instance.responseSelection,
      ...(description !== instance.responseSelection?.description && { description }),
      ...(responseMatch !== instance.responseSelection?.responseMatch && {
        responseMatch,
      }),
      ...(jsonPath !== instance.responseSelection?.jsonPath && { jsonPath }),
      ...(xPath !== instance.responseSelection?.xPath && { xPath }),
      hash: isPrivateInformation ? 'oprf' : '',
    };

    dispatch(
      updateResponseSelection({
        index: instance_index,
        responseSelection: newResponseSelection,
      })
    );
    toast({
      title: 'Variable updated',
      description: 'The variable has been updated successfully',
      status: 'success',
      position: 'top-right',
    });
    setModalOpen(false);
  };

  // Add debounced values for validation inputs
  const debouncedResponseMatch = useDebounce(responseMatch, 500);
  const debouncedJsonPath = useDebounce(jsonPath, 500);
  const debouncedXPath = useDebounce(xPath, 500);

  const validateResponseSelection = useCallback(
    (
      responseBody: string,
      responseMatch: string,
      jsonPath: string,
      xPath: string,
      contentType: string
    ): ValidationResult => {
      const result: ValidationResult = {
        regex: false,
        jsonPath: false,
        xPath: false,
      };

      // Validate Response Match (regex)
      if (responseMatch) {
        try {
          let modifiedResponseMatch: string = responseMatch;
          try {
            modifiedResponseMatch = JSON.parse(responseMatch);
          } catch {
            // Keep original if not JSON
          }
          const responseRegex = modifiedResponseMatch.replace(/{{\w+}}/g, '(.*?)');
          const regex = new RegExp(responseRegex);
          result.regex = regex.test(responseBody);
        } catch {
          result.regex = false;
        }
      }

      // Validate JsonPath
      if (contentType.includes('json') && jsonPath) {
        try {
          const bodyObj = JSON.parse(responseBody);
          const cleanPath = jsonPath.startsWith('$') ? jsonPath.substring(1) : jsonPath;
          const value = getValueByPath(bodyObj, cleanPath);
          result.jsonPath = value !== undefined;
        } catch {
          result.jsonPath = false;
        }
      }

      // Validate XPath
      if (contentType.includes('text/html') && xPath) {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(responseBody, 'text/html');
          const xpathResult = document.evaluate(
            xPath,
            doc,
            null,
            XPathResult.ANY_TYPE,
            null
          );
          result.xPath = xpathResult.iterateNext() !== null;
        } catch {
          result.xPath = false;
        }
      }

      return result;
    },
    []
  );

  // 2. Memoize the selected network request data
  const networkRequestData = useMemo(
    () => ({
      responseBody: selectedNetworkRequest[selectedNetworkIndex]?.response?.body,
      contentType: selectedNetworkRequest[selectedNetworkIndex]?.contentType || '',
    }),
    [selectedNetworkRequest, selectedNetworkIndex]
  );

  // 3. Optimize validation effect
  useEffect(() => {
    if (!instance?.responseSelection) return;

    const validationResult = validateResponseSelection(
      networkRequestData.responseBody,
      debouncedResponseMatch,
      debouncedJsonPath,
      debouncedXPath,
      networkRequestData.contentType
    );

    setValidation(validationResult);
  }, [
    debouncedResponseMatch,
    debouncedJsonPath,
    debouncedXPath,
    networkRequestData,
    validateResponseSelection,
  ]);

  // 4. Memoize handlers
  const handleDeleteInstance = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(deleteSelectedInstance(instance_index));
    },
    [dispatch, instance_index]
  );

  const handleVariableNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setVariableName(e.target.value);
    },
    []
  );

  // 1. Split into two functions - logic and render
  const getValidationStatus = () => {
    if (selectedNetworkRequest[selectedNetworkIndex]?.contentType.includes('json')) {
      if (validation.jsonPath === null && validation.regex === null) {
        return 'loading';
      }
      return validation.jsonPath && validation.regex ? 'valid' : 'invalid';
    }

    if (selectedNetworkRequest[selectedNetworkIndex]?.contentType.includes('text/html')) {
      if (validation.xPath === null && validation.regex === null) {
        return 'loading';
      }
      return validation.xPath && validation.regex ? 'valid' : 'invalid';
    }

    return 'invalid';
  };

  // 2. Create separate components for the icons
  const ValidationIcon = memo(({ status }: { status: string }) => {
    switch (status) {
      case 'loading':
        return (
          <div className="animate-spin w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full shrink-0" />
        );
      case 'valid':
        return <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />;
      case 'invalid':
        return <XCircle className="w-3 h-3 text-red-500 shrink-0" />;
      default:
        return null;
    }
  });

  return (
    <Dialog open={modalOpen} onOpenChange={setModalOpen}>
      <SidebarMenuButton className="">
        <DialogTrigger className="w-full">
          <div className="flex justify-between">
            <span className="flex items-center gap-2">
              <ValidationIcon status={getValidationStatus()} />
              <p className="text-xs">{variableName}</p>
            </span>
            <div>
              <button
                onClick={handleDeleteInstance}
                className="p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-gray-200 transition-opacity"
              >
                <X className="w-3 h-3 text-gray-500" />
              </button>
            </div>
          </div>
        </DialogTrigger>
      </SidebarMenuButton>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Variable Details</DialogTitle>
          <DialogDescription>
            Edit the variable details and validation rules.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* General Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">General Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="detail-name">Name</Label>
                <Input
                  id="detail-name"
                  value={variableName}
                  onChange={handleVariableNameChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="detail-description">Description</Label>
                <Input
                  id="detail-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="private"
                  isChecked={isPrivateInformation}
                  onChange={(e) => setIsPrivateInformation(e.target.checked)}
                />
                <Label htmlFor="private">Private Information</Label>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Technical Details</h3>

            <div className="space-y-4">
              {/* <div className="flex items-center space-x-2">
                                <div className="flex-grow">
                                    <Label htmlFor="regex">Regular Expression</Label>
                                    <Input
                                        id="regex"
                                        className={'border-input'}
                                        value={responseMatch}
                                        onChange={(e) => setResponseMatch(e.target.value)}
                                    />
                                </div>
                                {validation.regex ? (
                                    <Check className="w-5 h-5 text-green-500 mt-6" />
                                ) : (
                                    <X className="w-5 h-5 text-destructive mt-6" />
                                )}
                            </div> */}

              {/* only show if content type is json */}
              {selectedNetworkRequest[selectedNetworkIndex]?.contentType.includes(
                'json'
              ) && (
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <div className="flex-grow">
                      <Label htmlFor="jsonpath">JSONPath</Label>
                      <Input
                        id="jsonpath"
                        className={'border-input'}
                        value={jsonPath}
                        onChange={(e) => setJsonPath(e.target.value)}
                      />
                    </div>
                    {validation.jsonPath ? (
                      <Check className="w-5 h-5 text-green-500 mt-6" />
                    ) : (
                      <X className="w-5 h-5 text-destructive mt-6" />
                    )}
                  </div>
                  {!validation.jsonPath && jsonPath && (
                    <p className="text-sm text-destructive">
                      {VALIDATION_MESSAGES.jsonPath}
                    </p>
                  )}
                </div>
              )}

              {/* only show if content type is html */}
              {selectedNetworkRequest[selectedNetworkIndex]?.contentType.includes(
                'html'
              ) && (
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <div className="flex-grow">
                      <Label htmlFor="xpath">XPath</Label>
                      <Input
                        id="xpath"
                        className={'border-input'}
                        value={xPath}
                        onChange={(e) => setXPath(e.target.value)}
                      />
                    </div>
                    {validation.xPath ? (
                      <Check className="w-5 h-5 text-green-500 mt-6" />
                    ) : (
                      <X className="w-5 h-5 text-destructive mt-6" />
                    )}
                  </div>
                  {!validation.xPath && xPath && (
                    <p className="text-sm text-destructive">
                      {VALIDATION_MESSAGES.xPath}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="flex-grow">
                    <Label htmlFor="responseMatch">Response Match</Label>
                    <Input
                      id="responseMatch"
                      className={'border-input'}
                      value={responseMatch}
                      onChange={(e) => setResponseMatch(e.target.value)}
                    />
                  </div>
                  {validation.regex ? (
                    <Check className="w-5 h-5 text-green-500 mt-6" />
                  ) : (
                    <X className="w-5 h-5 text-destructive mt-6" />
                  )}
                </div>
                {!validation.regex && responseMatch && (
                  <p className="text-sm text-destructive">
                    {VALIDATION_MESSAGES.responseMatch}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleUpdateVariable}>Update Variable</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
